import {Injectable} from '@angular/core';
import type * as am5 from '@amcharts/amcharts5';
import type * as am5xy from '@amcharts/amcharts5/xy';
import type * as am5percent from '@amcharts/amcharts5/percent';
import {MatDialog} from '@angular/material/dialog';
import {ComponentPortal} from '@angular/cdk/portal';
import {Overlay} from '@angular/cdk/overlay';
import {Enum} from '../models/enum.model';
import {AuthService} from './auth.service';
import {ParameterFieldEnum, ParameterTypeEnum} from '../enums/parameterType.enum';
import {SensorDataTypeEnum} from '../enums/sensorDataType.enum';
import {UserPreferences} from '../models/userPreferences.model';
import {NodeRecord, SensorRecord} from '../models/sensorRecord.model';
import {ToastService} from './toast.service';
import {RecordDto} from '../models/recordDto.model';
import {OnZoom} from '../components/chartBase/onZoom.interface';
import {DeviceValue} from '../models/deviceValue.model';
import {ResizedEvent} from 'angular-resize-event-package';
import {IMapComponent} from '../components/chartBase/mapComponent.interface';
import {VideoResource} from '../models/videoResource.model';
import {SensorRecordService} from './sensorRecord.service';
import {Comment} from '../models/comment.model';
import {Lookup} from '../models/lookup.model';
import {Subject} from 'rxjs';
import {normalizeThresholdColorHex} from '../shared/utils/threshold-color.util';
import {switchMap, tap} from 'rxjs/operators';
import {Note} from '../models/note.model';
import {NoteService} from './note.service';
import {TaskAssignmentExposureContribution} from '../models/taskAssignment.model';
import {CommonService} from './common.service';
import {CommentService} from './comment.service';
import {UploadService} from './upload.service';
import {ComponentNames} from '../shared/constants/componentNames.constants';
import {ValueConversionService} from './value-conversion.service';
import {globalConst} from '../shared/constants/global.constants';
import {DateFormatService} from './dateFormat.service';
import {escapeHtml} from '../shared/helpers/html-escape.helper';
import {CommentsDialogComponent, ICommentDialogData} from '../common-components/comments-dialog/comments-dialog.component';
import {INoteDialogData, INoteDialogResult, NotesDialogComponent} from '../common-components/notes-dialog/notes-dialog.component';
import {ChartAnnotationPopupComponent} from '../shared/chart/chart-annotation-popup/chart-annotation-popup.component';

@Injectable()
export class ChartService {
  timestamp = 0;
  initialTimestamp = 0;
  analyticsTimestamp = 0;
  analyticsVerticalTimestamp = 0;
  resizeTimestamp: { [key: string]: number | undefined } = {};
  private zoomTimeoutId: ReturnType<typeof setTimeout> | undefined;
  private initialZoomTimeoutId: ReturnType<typeof setTimeout> | undefined;
  private analyticsZoomTimeoutId: ReturnType<typeof setTimeout> | undefined;
  private analyticsVerticalZoomTimeoutId: ReturnType<typeof setTimeout> | undefined;
  private resizeTimeoutIds: { [key: string]: ReturnType<typeof setTimeout> | undefined } = {};
  refreshUI: Subject<boolean>;
  clearNotes = new Subject<boolean>();
  tasksAssignmentData: TaskAssignmentExposureContribution[];
  commentsRange: { [key: string]: am5.DataItem<am5xy.IDateAxisDataItem> } = {};
  commentPopupRange: am5.DataItem<am5xy.IDateAxisDataItem>;
  private endpointId: string;
  private comments: Record<string, Record<string, Comment[]>>;
  private commentsCount: Record<string, number> = {};

  private _am5: typeof import('@amcharts/amcharts5') | null = null;
  private _am5xy: typeof import('@amcharts/amcharts5/xy') | null = null;
  private _am5percent: typeof import('@amcharts/amcharts5/percent') | null = null;
  private _am5radar: typeof import('@amcharts/amcharts5/radar') | null = null;
  private _am5themes_Animated: typeof import('@amcharts/amcharts5/themes/Animated')['default'] | null = null;
  private _am5plugins_exporting: typeof import('@amcharts/amcharts5/plugins/exporting') | null = null;
  private _amchartsLoadPromise: Promise<void> | null = null;

  constructor(private authService: AuthService,
              private toastService: ToastService,
              private noteService: NoteService,
              private commonService: CommonService,
              private commentService: CommentService,
              private sensorRecordService: SensorRecordService,
              private uploadService: UploadService,
              private valueConversionService: ValueConversionService,
              private dateFormatService: DateFormatService,
              private matDialog: MatDialog,
              private overlay: Overlay) {
  }

  // amCharts 5 has no global "disable all animations" switch equivalent to v4's
  // core.options.animationsEnabled — animation is opt-in per root via the Animated
  // theme. Roots created here deliberately don't apply it (root.setThemes([])),
  // matching the v4 default this file relied on everywhere.
  private loadAmCharts5(): Promise<void> {
    if (this._am5 && this._am5xy) {
      return Promise.resolve();
    }
    if (this._amchartsLoadPromise) {
      return this._amchartsLoadPromise;
    }
    this._amchartsLoadPromise = Promise.all([
      import(/* webpackChunkName: "amcharts" */ '@amcharts/amcharts5'),
      import(/* webpackChunkName: "amcharts" */ '@amcharts/amcharts5/xy'),
      import(/* webpackChunkName: "amcharts" */ '@amcharts/amcharts5/percent'),
      import(/* webpackChunkName: "amcharts" */ '@amcharts/amcharts5/radar'),
      import(/* webpackChunkName: "amcharts" */ '@amcharts/amcharts5/themes/Animated'),
      import(/* webpackChunkName: "amcharts" */ '@amcharts/amcharts5/plugins/exporting')
    ]).then(([am5Module, am5xyModule, am5percentModule, am5radarModule, animated, exporting]) => {
      this._am5 = am5Module;
      this._am5xy = am5xyModule;
      this._am5percent = am5percentModule;
      this._am5radar = am5radarModule;
      this._am5themes_Animated = animated.default;
      this._am5plugins_exporting = exporting;
    }).catch(err => {
      this._amchartsLoadPromise = null;
      throw err;
    });
    return this._amchartsLoadPromise;
  }

  async getAmCharts5(): Promise<{
    am5: typeof import('@amcharts/amcharts5');
    am5xy: typeof import('@amcharts/amcharts5/xy');
    am5percent: typeof import('@amcharts/amcharts5/percent');
    am5radar: typeof import('@amcharts/amcharts5/radar');
    am5plugins_exporting: typeof import('@amcharts/amcharts5/plugins/exporting');
  }> {
    await this.loadAmCharts5();
    return {
      am5: this._am5!,
      am5xy: this._am5xy!,
      am5percent: this._am5percent!,
      am5radar: this._am5radar!,
      am5plugins_exporting: this._am5plugins_exporting!,
    };
  }

  createRoot(target: string | HTMLElement): am5.Root {
    const am5Ns = this._am5!;
    const root = am5Ns.Root.new(target);
    root.setThemes([]);
    root._logo?.dispose();
    return root;
  }

  cleanup(): void {
    clearTimeout(this.zoomTimeoutId);
    clearTimeout(this.initialZoomTimeoutId);
    clearTimeout(this.analyticsZoomTimeoutId);
    clearTimeout(this.analyticsVerticalZoomTimeoutId);
    Object.values(this.resizeTimeoutIds).forEach(id => clearTimeout(id));
    this.resizeTimeoutIds = {};
  }

  /**
   * Builds the chart annotation-notes "Task Assignment" dropdown options. Each option
   * enumerates an individual TaskAssignment so its value is a real TaskAssignment.Id — the
   * FK target of Note.taskAssignmentId and the key the getNotes read path queries by.
   */
  private buildTaskAssignmentOptions(): string {
    let options = '';
    for (let i = 0; i < this.tasksAssignmentData?.length; i++) {
      const contribution = this.tasksAssignmentData[i];
      (contribution.taskAssignments || []).forEach((assignment) => {
        const hasRange = assignment.startDate && assignment.endDate;
        const timeRange = hasRange
          ? ` (${escapeHtml(this.dateFormatService.formatTimestampToString(assignment.startDate))}` +
            ` - ${escapeHtml(this.dateFormatService.formatTimestampToString(assignment.endDate))})`
          : '';
        options += `<option value="${escapeHtml(assignment.id)}">${escapeHtml(contribution.taskName)}${timeRange}</option>`;
      });
    }
    return options;
  }

  /**
   * Lookup-shaped equivalent of buildTaskAssignmentOptions(), for the amCharts5 MatDialog
   * annotation forms (NotesDialogComponent) which take a resolved Lookup[] rather than raw
   * HTML <option> markup.
   */
  private buildTaskAssignmentLookups(): Lookup[] {
    const lookups: Lookup[] = [];
    (this.tasksAssignmentData ?? []).forEach((contribution) => {
      (contribution.taskAssignments || []).forEach((assignment) => {
        const hasRange = assignment.startDate && assignment.endDate;
        const timeRange = hasRange
          ? ` (${this.dateFormatService.formatTimestampToString(assignment.startDate)}` +
            ` - ${this.dateFormatService.formatTimestampToString(assignment.endDate)})`
          : '';
        lookups.push({id: assignment.id, name: `${contribution.taskName}${timeRange}`} as Lookup);
      });
    });
    return lookups;
  }

  getSensorParameters(sensorType: string[]) {
    let parameters = this.getParameters();
    if (!sensorType.includes(SensorDataTypeEnum.NoiseTimeseries)) {
      parameters = parameters.filter(x => x.id !== ParameterTypeEnum.Lavg);
    }
    if (!sensorType.includes(SensorDataTypeEnum.GasData)) {
      parameters = parameters.filter(x =>
        x.id !== ParameterTypeEnum.TVOC &&
        x.id !== ParameterTypeEnum.CO2 &&
        x.id !== ParameterTypeEnum.H2S &&
        x.id !== ParameterTypeEnum.NO);
    }
    if (!sensorType.includes(SensorDataTypeEnum.AirNowMonitoring)) {
      parameters = parameters.filter(x =>
        x.id !== ParameterTypeEnum.Ozone &&
        x.id !== ParameterTypeEnum.SO2 &&
        x.id !== ParameterTypeEnum.NO2 &&
        x.id !== ParameterTypeEnum.CO);
    }
    if (!sensorType.includes(SensorDataTypeEnum.ParticulateTimeseries) && !sensorType.includes(SensorDataTypeEnum.OpcR2)) {
      parameters = parameters.filter(x =>
        x.id !== ParameterTypeEnum.Pm1 &&
        x.id !== ParameterTypeEnum.RespirablePm &&
        x.id !== ParameterTypeEnum.Humidity &&
        x.id !== ParameterTypeEnum.Temperature &&
        x.id !== ParameterTypeEnum.FeelsLike);
    }
    if (!sensorType.includes(SensorDataTypeEnum.ParticulateTimeseries) && !sensorType.includes(SensorDataTypeEnum.OpcR2) && !sensorType.includes(SensorDataTypeEnum.AirNowMonitoring)) {
      parameters = parameters.filter(x =>
        x.id !== ParameterTypeEnum.Pm2_5 &&
        x.id !== ParameterTypeEnum.Pm10);
    }
    if (!sensorType.includes(SensorDataTypeEnum.TrolexSensorRecords)) {
      parameters = parameters.filter(x =>
        x.id !== ParameterTypeEnum.MeasuredRCS &&
        x.id !== ParameterTypeEnum.FlowRate);
    }

    return parameters;
  }

  getParameters() {
    const parameters: Enum[] = [];

    parameters.push(new Enum(ParameterTypeEnum.Pm1, this.getParameterName(ParameterTypeEnum.Pm1)));
    parameters.push(new Enum(ParameterTypeEnum.Pm2_5, this.getParameterName(ParameterTypeEnum.Pm2_5)));
    parameters.push(new Enum(ParameterTypeEnum.Pm10, this.getParameterName(ParameterTypeEnum.Pm10)));
    parameters.push(new Enum(ParameterTypeEnum.RespirablePm, this.getParameterName(ParameterTypeEnum.RespirablePm)));
    parameters.push(new Enum(ParameterTypeEnum.Humidity, this.getParameterName(ParameterTypeEnum.Humidity)));
    parameters.push(new Enum(ParameterTypeEnum.Temperature, this.getParameterName(ParameterTypeEnum.Temperature)));
    parameters.push(new Enum(ParameterTypeEnum.FeelsLike, this.getParameterName(ParameterTypeEnum.FeelsLike)));
    parameters.push(new Enum(ParameterTypeEnum.Lavg, this.getParameterName(ParameterTypeEnum.Lavg)));
    parameters.push(new Enum(ParameterTypeEnum.Ozone, this.getParameterName(ParameterTypeEnum.Ozone)));
    parameters.push(new Enum(ParameterTypeEnum.SO2, this.getParameterName(ParameterTypeEnum.SO2)));
    parameters.push(new Enum(ParameterTypeEnum.NO2, this.getParameterName(ParameterTypeEnum.NO2)));
    parameters.push(new Enum(ParameterTypeEnum.CO, this.getParameterName(ParameterTypeEnum.CO)));
    parameters.push(new Enum(ParameterTypeEnum.CO2, this.getParameterName(ParameterTypeEnum.CO2)));
    parameters.push(new Enum(ParameterTypeEnum.TVOC, this.getParameterName(ParameterTypeEnum.TVOC)));
    parameters.push(new Enum(ParameterTypeEnum.H2S, this.getParameterName(ParameterTypeEnum.H2S)));
    parameters.push(new Enum(ParameterTypeEnum.NO, this.getParameterName(ParameterTypeEnum.NO)));
    parameters.push(new Enum(ParameterTypeEnum.BinMass, this.getParameterName(ParameterTypeEnum.BinMass)));
    parameters.push(new Enum(ParameterTypeEnum.MeasuredRCS, this.getParameterName(ParameterTypeEnum.MeasuredRCS)));
    parameters.push(new Enum(ParameterTypeEnum.FlowRate, this.getParameterName(ParameterTypeEnum.FlowRate)));

    return parameters;
  }

  async createChart(id: string): Promise<am5xy.XYChart> {
    const {am5: am5Ns, am5xy: am5xyNs} = await this.getAmCharts5();
    am5Ns.addLicense('ch-custom-attribution');
    const root = this.createRoot(id);

    const chart = root.container.children.push(am5xyNs.XYChart.new(root, {
      panX: true,
      panY: false,
      wheelX: 'panX',
      wheelY: 'zoomX',
    }));
    const xRenderer = am5xyNs.AxisRendererX.new(root, {});
    const dateAxis = chart.xAxes.push(am5xyNs.DateAxis.new(root, {
      baseInterval: {timeUnit: 'second', count: 1},
      renderer: xRenderer,
      tooltip: am5Ns.Tooltip.new(root, {}),
    }));

    const preferencesSubscription = this.dateFormatService.userPreferencesSubject.subscribe(() => {
      const monthDayFormat = this.dateFormatService.monthDayFormat;
      dateAxis.set('tooltipDateFormats', {
        ...dateAxis.get('tooltipDateFormats'),
        second: `${monthDayFormat} ${this.dateFormatService.userTimeWithSecondsFormat}`,
        day: monthDayFormat,
        week: monthDayFormat,
      });
      const timeZone = this.dateFormatService.getTimeZone();
      if (timeZone && timeZone !== root.timezone?.name) {
        root.timezone = am5Ns.Timezone.new(timeZone);
        chart.series.each((series) => series.data.setAll([...series.data.values]));
      }
    });
    dateAxis.addDisposer(new am5Ns.Disposer(() => preferencesSubscription.unsubscribe()));
    xRenderer.grid.template.set('location', 0);
    xRenderer.labels.template.set('location', 0);

    const valueAxis = chart.yAxes.push(am5xyNs.ValueAxis.new(root, {
      renderer: am5xyNs.AxisRendererY.new(root, {}),
    }));
    const axisLabel = am5Ns.Label.new(root, {
      rotation: -90,
      text: '',
      fill: am5Ns.color(this.commonService.colors.primary),
      fontWeight: 'bold',
      y: am5Ns.p50,
      centerX: am5Ns.p50,
    });
    valueAxis.children.unshift(axisLabel);
    valueAxis.get('renderer').labels.template.set('fill', am5Ns.color(this.commonService.colors.primary));

    const legend = chart.children.push(am5Ns.Legend.new(root, {}));
    legend.set('visible', false);
    chart.set('cursor', am5xyNs.XYCursor.new(root, {xAxis: dateAxis, behavior: 'zoomX'}));

    const {am5plugins_exporting: exportingNs} = await this.getAmCharts5();
    exportingNs.Exporting.new(root, {
      menu: exportingNs.ExportingMenu.new(root, {}),
    });

    return chart;
  }

  async createHeatMapChart(id: string): Promise<am5xy.XYChart> {
    const {am5: am5Ns, am5xy: am5xyNs, am5plugins_exporting: exportingNs} = await this.getAmCharts5();
    const root = this.createRoot(id);
    const chart = root.container.children.push(am5xyNs.XYChart.new(root, {
      panX: false,
      panY: false,
    }));

    const xAxis = chart.xAxes.push(am5xyNs.CategoryAxis.new(root, {
      categoryField: 'weekday',
      renderer: am5xyNs.AxisRendererX.new(root, {minGridDistance: 20}),
    }));
    xAxis.get('renderer').grid.template.set('visible', false);

    const yAxis = chart.yAxes.push(am5xyNs.CategoryAxis.new(root, {
      categoryField: 'hour',
      renderer: am5xyNs.AxisRendererY.new(root, {inversed: true, minGridDistance: 20}),
    }));
    yAxis.get('renderer').grid.template.set('visible', false);

    exportingNs.Exporting.new(root, {
      menu: exportingNs.ExportingMenu.new(root, {}),
    });

    return chart;
  }

  async drawHeatMapChart(chart: am5xy.XYChart, sensorData: Record<string, unknown>[], param: string): Promise<void> {
    const {am5: am5Ns, am5xy: am5xyNs} = await this.getAmCharts5();
    const root = chart.root;
    const xAxis = chart.xAxes.getIndex(0) as am5xy.CategoryAxis<am5xy.AxisRendererX>;
    const yAxis = chart.yAxes.getIndex(0) as am5xy.CategoryAxis<am5xy.AxisRendererY>;

    const series = chart.series.push(am5xyNs.ColumnSeries.new(root, {
      xAxis,
      yAxis,
      categoryXField: 'weekday',
      categoryYField: 'hour',
      valueField: param,
    }));

    const bgColor = root.interfaceColors.get('background');
    series.columns.template.setAll({
      strokeWidth: 1,
      strokeOpacity: 0.2,
      stroke: bgColor,
      tooltipText: '{weekday}, {hour}: {value}',
      width: am5Ns.percent(100),
      height: am5Ns.percent(100),
    });

    series.set('heatRules', [{
      target: series.columns.template,
      key: 'fill',
      min: bgColor,
      max: am5Ns.color(this.commonService.colors.primary),
      dataField: 'value',
    }]);

    const heatLegend = chart.rightAxesContainer.children.push(am5Ns.HeatLegend.new(root, {
      orientation: 'vertical',
      marginLeft: 3,
      width: am5Ns.percent(50),
      height: am5Ns.percent(100),
      startColor: bgColor,
      endColor: am5Ns.color(this.commonService.colors.primary),
      startOpacity: 1,
      endOpacity: 1,
    }));

    series.columns.template.events.on('pointerover', (event) => {
      this.handleHover(event.target, heatLegend);
    });
    series.columns.template.events.on('click', (event) => {
      this.handleHover(event.target, heatLegend);
    });
    series.columns.template.events.on('pointerout', () => {
      heatLegend.showValue(NaN);
    });

    series.data.setAll(sensorData as Record<string, unknown>[]);
    chart.appear(0, 0);
    series.appear(0);
  }

  handleHover(column: am5.RoundedRectangle, heatLegend: am5.HeatLegend): void {
    const value = column.dataItem?.get('value' as never) as number | undefined;
    if (value != null && !isNaN(value)) {
      heatLegend.showValue(value);
    } else {
      heatLegend.showValue(NaN);
    }
  }

  createChartZoomEvent(chart: am5xy.XYChart, zoomComponent: OnZoom, zoomToValues = true): am5.IDisposer {
    const dateAxis = chart.xAxes.getIndex(0) as am5xy.DateAxis<am5xy.AxisRendererX>;

    const emit = () => {
      const myTime = (new Date()).getTime();
      this.timestamp = myTime;

      clearTimeout(this.zoomTimeoutId);
      this.zoomTimeoutId = setTimeout(() => {
        if (myTime !== this.timestamp) {
          return;
        }

        const timestampStart = dateAxis.getPrivate('selectionMin') as number | undefined;
        const timestampEnd = dateAxis.getPrivate('selectionMax') as number | undefined;

        if (!zoomToValues) {
          zoomComponent.onZoom({start: timestampStart, end: timestampEnd});
          return;
        }

        const seriesData = chart.series.getIndex(0)?.data.values as SensorRecord[] | undefined;
        if (!timestampStart || !timestampEnd || !seriesData?.length) {
          zoomComponent.onZoom([]);
          return;
        }

        const sensorRecords: SensorRecord[] = seriesData.filter((s: SensorRecord) =>
          s.timestamp > timestampStart && s.timestamp < timestampEnd);
        zoomComponent.onZoom(sensorRecords);
      }, 500);
    };

    const disposerMin = dateAxis.onPrivate('selectionMinFinal', emit);
    const disposerMax = dateAxis.onPrivate('selectionMaxFinal', emit);
    return new this._am5!.MultiDisposer([disposerMin, disposerMax]);
  }

  createChartInitialZoomEvent(chart: am5xy.XYChart, zoomComponent: OnZoom, zoomToValues = true): am5.IDisposer {
    const dateAxis = chart.xAxes.getIndex(0) as am5xy.DateAxis<am5xy.AxisRendererX>;

    const emit = () => {
      const timestampStart = dateAxis.getPrivate('selectionMin') as number | undefined;
      const timestampEnd = dateAxis.getPrivate('selectionMax') as number | undefined;

      if (!timestampStart || !timestampEnd) {
        return;
      }

      const myTime = (new Date()).getTime();
      this.initialTimestamp = myTime;

      clearTimeout(this.initialZoomTimeoutId);
      this.initialZoomTimeoutId = setTimeout(() => {
        if (myTime !== this.initialTimestamp) {
          return;
        }

        if (!zoomToValues) {
          zoomComponent.onInitialZoom({start: timestampStart, end: timestampEnd});
          return;
        }

        const seriesData = chart.series.getIndex(0)?.data.values as SensorRecord[] | undefined;
        if (!seriesData?.length) {
          zoomComponent.onInitialZoom([]);
          return;
        }

        const sensorRecords: SensorRecord[] = seriesData.filter((s: SensorRecord) =>
          s.timestamp > timestampStart && s.timestamp < timestampEnd);
        zoomComponent.onInitialZoom(sensorRecords);
      }, 3000);
    };

    const disposerMin = dateAxis.onPrivate('selectionMinFinal', emit);
    const disposerMax = dateAxis.onPrivate('selectionMaxFinal', emit);
    return new this._am5!.MultiDisposer([disposerMin, disposerMax]);
  }

  createChartAnalyticsEvent(chart: am5xy.XYChart, zoomComponent: OnZoom): am5.IDisposer {
    const dateAxis = chart.xAxes.getIndex(0) as am5xy.DateAxis<am5xy.AxisRendererX>;

    const emit = () => {
      const myTime = (new Date()).getTime();
      this.analyticsTimestamp = myTime;

      clearTimeout(this.analyticsZoomTimeoutId);
      this.analyticsZoomTimeoutId = setTimeout(() => {
        if (myTime !== this.analyticsTimestamp) {
          return;
        }

        zoomComponent.onAnalyticsHorizontalZoom();
      }, 500);
    };

    const disposerMin = dateAxis.onPrivate('selectionMinFinal', emit);
    const disposerMax = dateAxis.onPrivate('selectionMaxFinal', emit);
    return new this._am5!.MultiDisposer([disposerMin, disposerMax]);
  }

  createChartAnalyticsVerticalZoomEvent(chart: am5xy.XYChart, zoomComponent: OnZoom): am5.IDisposer {
    const valueAxis = chart.yAxes.getIndex(0) as am5xy.ValueAxis<am5xy.AxisRendererY>;

    const emit = () => {
      const myTime = (new Date()).getTime();
      this.analyticsVerticalTimestamp = myTime;

      clearTimeout(this.analyticsVerticalZoomTimeoutId);
      this.analyticsVerticalZoomTimeoutId = setTimeout(() => {
        if (myTime !== this.analyticsVerticalTimestamp) {
          return;
        }

        zoomComponent.onAnalyticsVerticalZoom();
      }, 500);
    };

    const disposerMin = valueAxis.onPrivate('selectionMinFinal', emit);
    const disposerMax = valueAxis.onPrivate('selectionMaxFinal', emit);
    return new this._am5!.MultiDisposer([disposerMin, disposerMax]);
  }

  onResized(chart: am5xy.XYChart, event: ResizedEvent, chartId: string): void {
    if (!chart || !event.oldRect) {
      return;
    }

    const myTime = (new Date()).getTime();
    this.resizeTimestamp[chartId] = myTime;

    clearTimeout(this.resizeTimeoutIds[chartId]);
    this.resizeTimeoutIds[chartId] = setTimeout(() => {
      if (myTime !== this.resizeTimestamp[chartId]) {
        return;
      }

      chart.root.resize();
    }, 100);
  }

  onSampleResized(chart: am5xy.XYChart, event: ResizedEvent, chartId: string): void {
    if (!chart || !event.oldRect) {
      return;
    }

    const myTime = (new Date()).getTime();
    this.resizeTimestamp[chartId] = myTime;

    clearTimeout(this.resizeTimeoutIds[chartId]);
    this.resizeTimeoutIds[chartId] = setTimeout(() => {
      const keys = Object.keys(this.commentsRange);
      const dateAxis = chart.xAxes.getIndex(0) as am5xy.DateAxis<am5xy.AxisRendererX>;
      const axisHeight = dateAxis.height();
      keys.forEach((key) => {
        const bulletSprite = this.commentsRange[key]?.get('bullet')?.get('sprite');
        if (bulletSprite) {
          bulletSprite.set('y', -((axisHeight > 0 ? (axisHeight - 52) : 100)));
        }
      });
      if (myTime !== this.resizeTimestamp[chartId]) {
        return;
      }

      chart.root.resize();
    }, 100);
  }

  async drawSensorRecordChart(chart: am5xy.XYChart, records: NodeRecord[] | SensorRecord[], parameters: ParameterTypeEnum[], mapComponent: IMapComponent, comments: Comment[] = [], chartFilters: { startDate?: Date; endDate?: Date } | null = null, refreshUi: Subject<boolean> = null, tasksAssignmentData: TaskAssignmentExposureContribution[] = null, componentName: string = '', zoomToDate: boolean = true, parameterName: string = null, parameterFieldName: string = null): Promise<void> {
    const {am5: am5Ns, am5xy: am5xyNs} = await this.getAmCharts5();
    const root = chart.root;

    const currentUser = this.authService.getCurrentUser();
    const params = parameters;

    if (componentName !== ComponentNames.deviceDetail && componentName !== ComponentNames.monitoringDashboard && componentName !== ComponentNames.nodeList) {
      if (this.commentPopupRange) {
        const dateAxisForCleanup = chart.xAxes.getIndex(0) as am5xy.DateAxis<am5xy.AxisRendererX>;
        dateAxisForCleanup.axisRanges.removeValue(this.commentPopupRange);
        this.commentPopupRange = null;
      }
      this.tasksAssignmentData = tasksAssignmentData;
      this.refreshUI = refreshUi;
      this.comments = {};
      this.endpointId = records && records.length ? records[0]?.endPointId : null;

      try {
        if (comments && Array.isArray(comments)) {
          comments.forEach((e) => {
            const xAxisFromDate = e.fromDate * 1000;
            const xAxisToDate = e.toDate * 1000;
            if (!this.comments[xAxisFromDate]) {
              this.comments[xAxisFromDate] = {};
            }
            if (!this.comments[xAxisFromDate][xAxisToDate]) {
              this.comments[xAxisFromDate][xAxisToDate] = [e];
            } else {
              this.comments[xAxisFromDate][xAxisToDate].push(e);
            }
          });
        }
      } catch (e) {
      }
    }

    this.disposeChartData(chart);
    const lineSeries: am5xy.LineSeries[] = [];
    let dateAxis: am5xy.DateAxis<am5xy.AxisRendererX> = null;
    if (componentName !== ComponentNames.deviceDetail) {
      dateAxis = chart.xAxes.getIndex(0) as am5xy.DateAxis<am5xy.AxisRendererX>;
    }

    for (let i = 0; i < params.length; i++) {
      const p = params[i];
      const s = chart.series.push(await this.createSeries(p, mapComponent, currentUser.preferences, dateAxis, chart, true, componentName, parameterName, parameterFieldName, i));
      lineSeries.push(s);
    }
    // v4's cursor showed each series' tooltip at the nearest data point automatically;
    // v5 requires the cursor to be told which series to snap to for the same hover behavior.
    chart.get('cursor')?.set('snapToSeries', lineSeries);

    if (componentName !== ComponentNames.endPoints && componentName !== ComponentNames.deviceDetail) {
      if (componentName !== ComponentNames.monitoringDashboard && componentName !== ComponentNames.nodeList) {
        const toggleAxes = () => {
          const disabled = !valueAxis.series.some((series) => series.get('visible'));
          valueAxis.set('visible', !disabled);
        };

        const valueAxis = chart.yAxes.push(am5xyNs.ValueAxis.new(root, {
          renderer: am5xyNs.AxisRendererY.new(root, {opposite: true}),
          min: 0,
          max: 100,
        }));
        const cumulativeLabel = am5Ns.Label.new(root, {
          rotation: -90,
          text: 'Cumulative Sum in %',
          fill: am5Ns.color(this.commonService.colors.red),
          y: am5Ns.p50,
          centerX: am5Ns.p50,
        });
        valueAxis.children.unshift(cumulativeLabel);
        valueAxis.get('renderer').labels.template.set('fill', am5Ns.color(this.commonService.colors.red));
        valueAxis.get('renderer').setAll({strokeOpacity: 1});

        const series = chart.series.push(am5xyNs.LineSeries.new(root, {
          name: 'Cumulative Sum',
          xAxis: dateAxis,
          yAxis: valueAxis,
          valueXField: 'timestamp',
          valueYField: 'cumulativeSumPercentage',
          stroke: am5Ns.color(this.commonService.colors.red),
          fill: am5Ns.color(this.commonService.colors.red),
          tooltip: am5Ns.Tooltip.new(root, {labelText: '{name}: [bold]{valueY}[/]'}),
        }));
        series.set('id', globalConst.cumulativeSumSeries);
        series.strokes.template.set('strokeWidth', 3);
        series.on('visible', toggleAxes);
      }

      await this.createLeftAxis(chart);
      if (componentName === ComponentNames.sampleDetails) {
        chart.set('scrollbarY', am5Ns.Scrollbar.new(root, {orientation: 'vertical'}));
      }
    }

    (chart.yAxes.getIndex(0) as am5xy.ValueAxis<am5xy.AxisRendererY>).children.each((child) => {
      if (child instanceof am5Ns.Label) {
        child.set('text', this.getValueAxisTitle(params));
      }
    });

    if (componentName === ComponentNames.deviceDetail) {
      await this.createXYChartScrollbar(chart, lineSeries);
      chart.set('scrollbarY', am5Ns.Scrollbar.new(root, {orientation: 'vertical'}));
    }

    if (records) {
      records.sort((a, b) => a.timestamp - b.timestamp);
      chart.series.each((series) => series.data.setAll(records as unknown as Record<string, unknown>[]));
      await this.createThresholdTrendLines(chart, records, params && params.length ? params[0] : null);
    }

    if (componentName !== ComponentNames.deviceDetail) {
      const enableZoomToDate = false;
      if (enableZoomToDate && zoomToDate) {
        setTimeout(() => {
          if (chartFilters && chartFilters.startDate && chartFilters.endDate) {
            dateAxis.zoomToDates(chartFilters.startDate, chartFilters.endDate);
          } else {
            try {
              dateAxis.zoomToDates(new Date(records[0].timestamp), new Date(records[records?.length - 1].timestamp));
            } catch (e) {
            }
          }
        }, 100);
      }
    }
  }

  async createThresholdTrendLines(chart, records, parameterType = null){
    const thresholds = this.commonService.parameterThresholds;
    for (let index = 0; index < thresholds.length; index++) {
      const threshold = thresholds[index];
      if (records && records.length) {
        const thresholdValue = this.valueConversionService.getParameterValueFromUserPreferences(threshold.minValue, parameterType)
        const trendData = [
          { date: records[0].timestamp, value: thresholdValue },
          { date: records[records.length - 1].timestamp, value: thresholdValue },
        ];
        const trendColor = this.commonService.getColorCode(normalizeThresholdColorHex(threshold.colorHex) ?? '#000000');
        await this.createTrendLine(chart, trendData, trendColor, index);
      }
    }
  }

  async createTrendLine(chart: am5xy.XYChart, data: { date: number; value: number }[], color: string, index: number): Promise<am5xy.LineSeries> {
    const {am5: am5Ns, am5xy: am5xyNs} = await this.getAmCharts5();
    const root = chart.root;
    const dateAxis = chart.xAxes.getIndex(0) as am5xy.DateAxis<am5xy.AxisRendererX>;
    const valueAxis = chart.yAxes.getIndex(0) as am5xy.ValueAxis<am5xy.AxisRendererY>;

    const trend = chart.series.push(am5xyNs.LineSeries.new(root, {
      xAxis: dateAxis,
      yAxis: valueAxis,
      valueXField: 'date',
      valueYField: 'value',
      stroke: am5Ns.color(`#${color}`),
    }));
    trend.set('id', `${globalConst.trendSeriesPrefix}${index}`);
    trend.strokes.template.set('strokeWidth', 2);
    trend.set('legendLabelText', '');
    trend.data.setAll(data);
    return trend;
  }

  hideShowThresholdTrendLines(chart: am5xy.XYChart): void {
    this.commonService.parameterThresholds.forEach((threshold, index) => {
      const series = chart.series.values.find((s) => s.get('id') === `${globalConst.trendSeriesPrefix}${index}`);
      this.hideShowSeries(series as am5xy.LineSeries);
    });
  }

  hideShowSeries(series: am5xy.LineSeries): void {
    if (series && series.get('visible')) {
      series.hide();
    } else if (series) {
      series.show();
    }
  }

  async createLeftAxis(chart: am5xy.XYChart, params: ParameterTypeEnum[] = null): Promise<void> {
    const {am5: am5Ns, am5xy: am5xyNs} = await this.getAmCharts5();
    const root = chart.root;
    const valueAxisLeft = (chart.yAxes.getIndex(0) ?? chart.yAxes.push(am5xyNs.ValueAxis.new(root, {
      renderer: am5xyNs.AxisRendererY.new(root, {}),
    }))) as am5xy.ValueAxis<am5xy.AxisRendererY>;
    const renderer = valueAxisLeft.get('renderer');
    renderer.setAll({strokeOpacity: 1, opposite: false});
    renderer.set('stroke', am5Ns.color(this.commonService.colors.primary));
    renderer.labels.template.set('fill', am5Ns.color(this.commonService.colors.primary));

    if (params) {
      valueAxisLeft.children.each((child) => {
        if (child instanceof am5Ns.Label) {
          child.set('text', this.getValueAxisTitle(params));
          child.set('fill', am5Ns.color(this.commonService.colors.primary));
        }
      });
    }
  }

  async drawVideoChart(chart: am5xy.XYChart, videoResources: VideoResource[]): Promise<void> {
    const {am5: am5Ns} = await this.getAmCharts5();
    this.disposeChartData(chart);

    // v4 cleared chart.exporting.menu.items here. amCharts5's Exporting instance is created
    // once per chart (in createChart) and not retained on the chart object itself, so there is
    // no handle here to clear its menu items against — a cosmetic gap, not a functional one.
    const dateAxis = chart.xAxes.getIndex(0) as am5xy.DateAxis<am5xy.AxisRendererX>;

    if (!videoResources || videoResources.length === 0) {
      return;
    }

    let minTimestamp = videoResources[0].timestampStart;
    let maxTimestamp = videoResources[0].timestampEnd;
    videoResources.forEach(resource => {
      if (resource.timestampStart < minTimestamp) {
        minTimestamp = resource.timestampStart;
      }
      if (resource.timestampEnd > maxTimestamp) {
        maxTimestamp = resource.timestampEnd;
      }

      const rangeItem = dateAxis.makeDataItem({
        value: resource.timestampStart,
        endValue: resource.timestampEnd,
      });
      dateAxis.createAxisRange(rangeItem);
      rangeItem.get('axisFill')?.setAll({
        visible: true,
        fill: am5Ns.color(this.commonService.colors.red),
        fillOpacity: 0.2,
      });
    });

    dateAxis.set('min', minTimestamp);
    dateAxis.set('max', maxTimestamp);
    dateAxis.set('strictMinMax', true);
  }

  async createXYChartScrollbar(chart: am5xy.XYChart, lineSeries: am5xy.XYSeries[]): Promise<void> {
    const {am5: am5Ns} = await this.getAmCharts5();
    const root = chart.root;
    const scrollbarX = am5Ns.Scrollbar.new(root, {orientation: 'horizontal'});
    chart.set('scrollbarX', scrollbarX);
    // amCharts5's Scrollbar has no built-in nested "preview chart" the way v4's
    // XYChartScrollbar did — lineSeries is retained here only to preserve the
    // v4 method signature for callers; a faithful mini-chart-preview scrollbar
    // would need a second, separate XYChart wired manually.
    void lineSeries;
  }

  disposeChartData(chart: am5xy.XYChart): void {
    chart.set('scrollbarX', undefined);
    chart.set('scrollbarY', undefined);

    while (chart.series.length > 0) {
      const seriesToDispose = chart.series.removeIndex(0);
      seriesToDispose.dispose();
    }

    const dateAxis = chart.xAxes.getIndex(0) as am5xy.DateAxis<am5xy.AxisRendererX>;
    if (dateAxis) {
      dateAxis.axisRanges.each((range) => dateAxis.axisRanges.removeValue(range));
    }
  }

  disposeEvent(disposer: am5.IDisposer): void {
    if (disposer && !disposer.isDisposed()) {
      disposer.dispose();
    }
  }

  getValueAxisTitle(selectedParams: number[]): string {
    if (!this.validateParams(selectedParams)) {
      return 'Value';
    }
    const unit = this.valueConversionService.getParameterUnit(selectedParams[0]);
    switch (selectedParams[0]) {
      case ParameterTypeEnum.Humidity:
        return `Humidity [[${unit}]]`;
      case ParameterTypeEnum.Temperature:
        return `Temperature [[${unit}]]`;
      case ParameterTypeEnum.FeelsLike:
        return `Feels Like [[${unit}]]`;
      case ParameterTypeEnum.Lavg:
        return `Average Sound Level [[${unit}]]`;
      case ParameterTypeEnum.Ozone:
        return `Ozone [[${unit}]]`;
      case ParameterTypeEnum.SO2:
        return `SO2 [[${unit}]]`;
      case ParameterTypeEnum.NO2:
        return `NO2 [[${unit}]]`;
      case ParameterTypeEnum.CO:
        return `CO [[${unit}]]`;
      case ParameterTypeEnum.TVOC:
        return `TVOC [[${unit}]]`;
      case ParameterTypeEnum.CO2:
        return `CO2 [[${unit}]]`;
      case ParameterTypeEnum.H2S:
        return `H2S [[${unit}]]`;
      case ParameterTypeEnum.NO:
        return `NO [[${unit}]]`;
      default:
        return `Concentration [[${unit}]]`;
    }
  }

  async createSeries(paramId: ParameterTypeEnum, mapComponent: IMapComponent, preferences: UserPreferences, dateAxis: am5xy.DateAxis<am5xy.AxisRendererX> = null, chart: am5xy.XYChart, hideBullets: boolean = false, componentName: string = '', parameterName: string = null, parameterFieldName: string = null, paramIndex = 0): Promise<am5xy.LineSeries> {
    const {am5: am5Ns, am5xy: am5xyNs} = await this.getAmCharts5();
    const root = chart.root;
    const commonColors = this.commonService.colors;
    const seriesName = parameterName || this.getParameterName(paramId);
    const paramField = parameterFieldName || this.getParameterField(paramId);
    const valueAxis = chart.yAxes.getIndex(0) as am5xy.ValueAxis<am5xy.AxisRendererY>;
    // dateAxis is intentionally null for deviceDetail (only used above for the right-click "add comment"
    // feature, which deviceDetail doesn't have) - v5 still requires an explicit xAxis on the series itself.
    const seriesXAxis = dateAxis ?? (chart.xAxes.getIndex(0) as am5xy.DateAxis<am5xy.AxisRendererX>);

    const color = this.getSeriesColor(paramIndex);
    const strokeColor = color ?? am5Ns.color(commonColors.greyDark);

    const series = am5xyNs.LineSeries.new(root, {
      name: seriesName,
      xAxis: seriesXAxis,
      yAxis: valueAxis,
      valueXField: 'timestamp',
      valueYField: paramField,
      connect: true,
      stroke: strokeColor,
      fill: strokeColor,
      minBulletDistance: 10,
      tooltip: am5Ns.Tooltip.new(root, {labelText: '{name}: [bold]{valueY}'}),
    });
    series.strokes.template.setAll({strokeWidth: 3, strokeDasharray: this.parseDashArray(this.getParameterStrokeDasharray(paramId, true))});
    const tooltip = series.get('tooltip');
    tooltip?.get('background')?.set('fill', am5Ns.color(commonColors.greyDark));

    if (componentName !== ComponentNames.deviceDetail && componentName !== ComponentNames.endPoints && componentName !== ComponentNames.monitoringDashboard && componentName !== ComponentNames.nodeList) {
      chart.plotContainer.set('interactive', true);
      chart.plotContainer.events.on('rightclick', () => {
        const cursorPositionX = chart.get('cursor')?.getPrivate('positionX') as number ?? 0;
        const dpStart = dateAxis.positionToDate(dateAxis.toAxisPosition(cursorPositionX));
        this.openAddCommentDialog(dateAxis, dpStart);
      });
    }

    if (componentName === ComponentNames.deviceDetail) {
      series.bullets.push(() => {
        const circle = am5Ns.Circle.new(root, {radius: 5, fill: am5Ns.color(commonColors.yellow), stroke: strokeColor});
        if (preferences.showMap && mapComponent) {
          circle.set('cursorOverStyle', 'pointer');
          circle.events.on('click', (event) => {
            const record = event.target.dataItem?.dataContext as SensorRecord;
            mapComponent.updateMap([record], true);
          });
          circle.adapters.add('fill', (fill, target) => {
            const record = target.dataItem?.dataContext as SensorRecord;
            if (!record) {
              return fill;
            }
            return record.lat <= 90 && (record.hdop === undefined || record.hdop < 20) && record.lat >= -90 &&
            record.lon <= 180 && record.lon >= -180 &&
            (record.lat !== 0 || record.lon !== 0)
              ? fill
              : am5Ns.color(commonColors.white);
          });
        }
        return am5Ns.Bullet.new(root, {sprite: circle});
      });
    } else {
      series.bullets.push(() => {
        const circle = am5Ns.Circle.new(root, {radius: 5, fill: am5Ns.color(commonColors.yellow), stroke: strokeColor});
        if (preferences.showMap && mapComponent) {
          circle.set('cursorOverStyle', 'pointer');
          circle.events.on('click', (event) => {
            const record = event.target.dataItem?.dataContext as SensorRecord;
            mapComponent.updateMap([record], true);
          });
          circle.adapters.add('fill', (fill, target) => {
            const record = target.dataItem?.dataContext as SensorRecord;
            if (!record) {
              return fill;
            }
            return record.lat <= 90 && (record.hdop === undefined || record.hdop < 20) && record.lat >= -90 &&
            record.lon <= 180 && record.lon >= -180 &&
            (record.lat !== 0 || record.lon !== 0)
              ? fill
              : am5Ns.color(commonColors.white);
          });
        }
        return am5Ns.Bullet.new(root, {sprite: circle});
      });

      this.commentsCount = {};
      this.commentsRange = {};
      if (componentName !== ComponentNames.monitoringDashboard && componentName !== ComponentNames.nodeList) {
        await this.addGroupCommentsLabelBullet(dateAxis, series);
      }
      if (!hideBullets) {
        await this.addLabelBulletForIndividualComment(series);
      }
    }

    return series;
  }

  /**
   * Opens the comment-creation MatDialog for a right-clicked chart position, highlighting the
   * clicked range on the date axis while the dialog is open (v5 replacement for v4's
   * series.openModal-based "Add Comment" popup).
   */
  private openAddCommentDialog(dateAxis: am5xy.DateAxis<am5xy.AxisRendererX>, startDatePointer: Date): void {
    const am5Ns = this._am5!;
    const endDate = new Date(dateAxis.getPrivate('selectionMax') as number);

    if (!this.commentPopupRange) {
      const rangeItem = dateAxis.makeDataItem({value: startDatePointer.getTime(), endValue: endDate.getTime()});
      dateAxis.createAxisRange(rangeItem);
      rangeItem.get('label')?.set('visible', false);
      this.commentPopupRange = rangeItem;
    }
    this.commentPopupRange.get('axisFill')?.setAll({
      visible: true,
      fill: am5Ns.color(this.commonService.colors.yellow),
      fillOpacity: 0.3,
    });
    this.commentPopupRange.set('value', startDatePointer.getTime());
    this.commentPopupRange.set('endValue', endDate.getTime());

    const dialogRef = this.matDialog.open<CommentsDialogComponent, ICommentDialogData, boolean>(CommentsDialogComponent, {
      data: {
        endPointId: this.endpointId,
        fromDate: startDatePointer,
        toDate: endDate,
        dateCreated: undefined,
        text: undefined,
      } as ICommentDialogData,
    });

    dialogRef.afterClosed().subscribe((saved) => {
      this.commentPopupRange?.get('axisFill')?.set('visible', false);
      if (saved && this.refreshUI) {
        this.refreshUI.next(true);
      }
    });
  }

  /**
   * Opens the note-creation MatDialog. v4's equivalent (openNewNotes, defined inline inside
   * createSeries) had no reachable call site — it was never wired to the right-click handler
   * or any other trigger — so this v5 replacement is likewise a standalone public method
   * available for a caller (e.g. a chart toolbar "Add Note" action) to invoke explicitly.
   */
  openAddNoteDialog(fromDate: Date, toDate: Date): void {
    const dialogRef = this.matDialog.open<NotesDialogComponent, INoteDialogData, INoteDialogResult>(NotesDialogComponent, {
      data: {
        fromDate,
        toDate,
        taskAssignmentOptions: this.buildTaskAssignmentLookups(),
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.note) {
        this.createNotes(result.note, result.file);
      }
    });
  }

  showHideComments(visibility: boolean): void {
    const keys = Object.keys(this.commentsRange);
    keys.forEach((key) => {
      const rangeItem = this.commentsRange[key];
      rangeItem?.get('axisFill')?.set('visible', visibility);
      rangeItem?.get('bullet')?.get('sprite')?.set('visible', visibility);
    });
  }

  clearCommentRanges(): void {
    const comments = document.querySelectorAll('.comments-notes-div .comment-list');
    for (let i = 0; i < comments.length; i++) {
      (comments[i] as HTMLElement).style.backgroundColor = 'unset';
    }
    const keys = Object.keys(this.commentsRange);
    keys.forEach((key) => {
      const rangeItem = this.commentsRange[key];
      rangeItem?.get('axisFill')?.set('fillOpacity', 0);
      rangeItem?.get('axisFill')?.set('visible', false);
    });
  }

  createNotes(notes: Note, notesFile: File): void {
    if (notes?.type > 0) {
      const fileExtension = notesFile?.name?.split('.')?.pop();
      const newFileName = `${Math.floor(Date.now() / 1000)}.${fileExtension}`;
      this.uploadService.getPreSignedURL(newFileName).pipe(
        switchMap((signedUrl) => {
          return this.uploadService.uploadFile(signedUrl, notesFile).pipe(
            tap(() => {
              notes.fileName = newFileName;
              notes.url = signedUrl.split('?')[0];
            })
          );
        })
      ).subscribe({
        next: () => {
          this.callCreateNotes(notes);
        },
        error: () => {
          this.toastService.showWarning('File upload failed.', true);
        }
      });
    } else {
      this.callCreateNotes(notes);
    }
  }

  createComment(comment: string, startDate: number, endDate: number): void {
    const payload = new Comment();
    payload.text = comment;
    payload.fromDate = startDate ? Math.ceil(startDate) : startDate;
    payload.toDate = endDate ? Math.ceil(endDate) : endDate;
    payload.endPointId = this.endpointId;
    this.commentService.create(payload).subscribe({
      next: () => {
        if (this.refreshUI) {
          this.refreshUI.next(true);
        }
      },
      error: () => {
      }
    });
  }

  updateComment(comment: Comment, onDone?: () => void): void {
    this.commentService.update(comment).subscribe({
      next: () => onDone?.(),
      error: () => onDone?.(),
    });
  }

  async createLineSeries(paramId: number, useSolidLine: boolean, name: string, index: number = 0, parameterName: string = null, chart: am5xy.XYChart): Promise<am5xy.LineSeries> {
    const {am5: am5Ns, am5xy: am5xyNs} = await this.getAmCharts5();
    const root = chart.root;
    const paramField = this.getParameterField(paramId);
    const seriesName = `${name} - ${parameterName || this.getParameterName(paramId)}`;

    const color = this.getSeriesColor(index);

    const series = am5xyNs.LineSeries.new(root, {
      name: seriesName,
      xAxis: chart.xAxes.getIndex(0) as am5xy.DateAxis<am5xy.AxisRendererX>,
      yAxis: chart.yAxes.getIndex(0) as am5xy.ValueAxis<am5xy.AxisRendererY>,
      valueXField: 'timestamp',
      valueYField: paramField,
      stroke: color,
      fill: color,
      minBulletDistance: 15,
      tooltip: am5Ns.Tooltip.new(root, {labelText: '{name}: [bold]{valueY}'}),
    });

    series.strokes.template.setAll({strokeWidth: 3, strokeDasharray: this.parseDashArray(this.getParameterStrokeDasharray(paramId, useSolidLine))});
    series.bullets.push(() => am5Ns.Bullet.new(root, {sprite: am5Ns.Circle.new(root, {radius: 5, fill: color})}));

    // v4's cursor showed each series' tooltip at the nearest data point automatically;
    // v5 requires the cursor to be told which series to snap to for the same hover behavior.
    const cursor = chart.get('cursor');
    cursor?.set('snapToSeries', [...(cursor.get('snapToSeries') ?? []), series]);

    return series;
  }

  private parseDashArray(dashArray: string): number[] | undefined {
    if (!dashArray) {
      return undefined;
    }
    return dashArray.split(',').map((n) => Number(n));
  }

  createDeviceValue(sensorRecords: SensorRecord[], params: number[]): DeviceValue {
    const deviceValue = new DeviceValue();
    params.forEach(p => {
      const values = sensorRecords.map(r => r[this.getParameterField(p)]).filter(v => v != null);
      deviceValue.average[p] = values.length
        ? values.reduce((a, b) => a + b, 0) / values.length : 0;
      deviceValue.peak[p] = values.length ? Math.max(...values) : 0;
    });
    return deviceValue;
  }

  getParameterName(parameterType: ParameterTypeEnum) {
    switch (parameterType) {
      case ParameterTypeEnum.Pm1:
        return 'PM1';
      case ParameterTypeEnum.Pm2_5:
        return 'PM2.5';
      case ParameterTypeEnum.Pm10:
        return 'PM10';
      case ParameterTypeEnum.RespirablePm:
        return 'Respirable PM';
      case ParameterTypeEnum.Humidity:
        return 'Humidity';
      case ParameterTypeEnum.Temperature:
        return 'Temperature';
      case ParameterTypeEnum.FeelsLike:
        return 'Feels Like';
      case ParameterTypeEnum.Latitude:
        return 'Latitude';
      case ParameterTypeEnum.Longitude:
        return 'Longitude';
      case ParameterTypeEnum.Lavg:
        return 'Average Sound Level';
      case ParameterTypeEnum.Ozone:
        return 'Ozone';
      case ParameterTypeEnum.CO:
        return 'CO';
      case ParameterTypeEnum.SO2:
        return 'SO2';
      case ParameterTypeEnum.NO2:
        return 'NO2';
      case ParameterTypeEnum.TVOC:
        return 'TVOC';
      case ParameterTypeEnum.CO2:
        return 'CO2';
      case ParameterTypeEnum.H2S:
        return 'H2S';
      case ParameterTypeEnum.NO:
        return 'NO';
      case ParameterTypeEnum.BatteryVoltage:
        return 'Battery Voltage';
      case ParameterTypeEnum.BinMass:
        return 'Bin Mass';
      case ParameterTypeEnum.ChargingVoltage:
        return 'Charging Voltage';
      case ParameterTypeEnum.ChargingCurrent:
        return 'Charging Current';
      case ParameterTypeEnum.SystemCurrent:
        return 'System Current';
      case ParameterTypeEnum.BatteryPercentage:
        return 'Battery Percentage';
      case ParameterTypeEnum.SignalStrength:
        return 'Signal Strength';
      case ParameterTypeEnum.PowerState:
        return 'Power State';
      case ParameterTypeEnum.BatteryCharging:
        return 'Battery Charging';
      case ParameterTypeEnum.Orientation:
        return 'Orientation';
      case ParameterTypeEnum.WindDirection:
        return 'Wind Direction';
      case ParameterTypeEnum.WindSpeed:
        return 'Wind Speed';
      case ParameterTypeEnum.Count0_3:
        return 'Count 0.3 um';
      case ParameterTypeEnum.Count0_5:
        return 'Count 0.5 um';
      case ParameterTypeEnum.Count1_0:
        return 'Count 1 um';
      case ParameterTypeEnum.Count2_5:
        return 'Count 2.5 um';
      case ParameterTypeEnum.Count5_0:
        return 'Count 5 um';
      case ParameterTypeEnum.Count10:
        return 'Count 10 um';
      case ParameterTypeEnum.DewPoint:
        return 'Dew Point';
      case ParameterTypeEnum.Pressure:
        return 'Pressure';
      case ParameterTypeEnum.Rainfall:
        return 'Rainfall';
      case ParameterTypeEnum.MeasuredRCS:
        return 'Measured RCS';
      case ParameterTypeEnum.FlowRate:
        return 'Flow Rate';
    }
  }

  getParameterField(parameterType: ParameterTypeEnum) {
    return ParameterFieldEnum[parameterType];
  }

  getParameterId(paramField: string) {
    return ParameterFieldEnum[paramField];
  }

  getParameterStrokeDasharray(parameterType: ParameterTypeEnum, useSolidLine: boolean) {
    if (useSolidLine) {
      return '';
    }
    switch (parameterType) {
      case ParameterTypeEnum.Pm1:
        return '';
      case ParameterTypeEnum.Pm2_5:
        return '2,4';
      case ParameterTypeEnum.Pm10:
        return '8,4';
      case ParameterTypeEnum.RespirablePm:
        return '8,4,2,4';
      default:
        return '';
    }
  }

  validateDateRange(fromDate: Date, toDate: Date) {
    if (fromDate >= toDate) {
      const message = 'Invalid date: From Date must be less than To Date.';
      this.toastService.showWarning(message);
      return false;
    }
    return true;
  }

  validateParams(selectedParams: number[]) {
    let isPmSelected = false;
    let isHumSelected = false;
    let isTempSelected = false;
    let isLavgSelected = false;
    let isPPBSelected = false;
    let isPPMSelected = false;

    selectedParams.forEach(p => {
      switch (p) {
        case ParameterTypeEnum.Pm1:
        case ParameterTypeEnum.Pm2_5:
        case ParameterTypeEnum.RespirablePm:
        case ParameterTypeEnum.Pm10:
        case ParameterTypeEnum.MeasuredRCS:
          isPmSelected = true;
          break;
        case ParameterTypeEnum.Humidity:
          isHumSelected = true;
          break;
        case ParameterTypeEnum.Temperature:
        case ParameterTypeEnum.FeelsLike:
          isTempSelected = true;
          break;
        case ParameterTypeEnum.Lavg:
          isLavgSelected = true;
          break;
        case ParameterTypeEnum.Ozone:
        case ParameterTypeEnum.NO2:
        case ParameterTypeEnum.SO2:
          isPPBSelected = true;
          break;
        case ParameterTypeEnum.CO:
        case ParameterTypeEnum.TVOC:
        case ParameterTypeEnum.CO2:
        case ParameterTypeEnum.H2S:
        case ParameterTypeEnum.NO:
          isPPMSelected = true;
          break;
        case ParameterTypeEnum.BinMass:
        case ParameterTypeEnum.BatteryVoltage:
        case ParameterTypeEnum.ChargingVoltage:
        case ParameterTypeEnum.ChargingCurrent:
        case ParameterTypeEnum.SystemCurrent:
        case ParameterTypeEnum.BatteryPercentage:
        case ParameterTypeEnum.SignalStrength:
        case ParameterTypeEnum.PowerState:
        case ParameterTypeEnum.BatteryCharging:
        case ParameterTypeEnum.Orientation:
        case ParameterTypeEnum.WindDirection:
        case ParameterTypeEnum.WindSpeed:
        case ParameterTypeEnum.Count0_3:
        case ParameterTypeEnum.Count0_5:
        case ParameterTypeEnum.Count1_0:
        case ParameterTypeEnum.Count2_5:
        case ParameterTypeEnum.Count5_0:
        case ParameterTypeEnum.Count10:
        case ParameterTypeEnum.DewPoint:
        case ParameterTypeEnum.Pressure:
          break;
      }
    });

    if ([isPmSelected, isTempSelected, isHumSelected, isLavgSelected, isPPBSelected, isPPMSelected].filter(x => x).length > 1) {
      return false;
    }
    return true;
  }

  getDefaultParameters() {
    const preferences = this.authService.getCurrentUser().preferences;
    return preferences && (preferences.parameters !== undefined || preferences.parameters !== null)
      ? preferences.parameters.map(x => x)
      : [ParameterTypeEnum.Pm1, ParameterTypeEnum.Pm2_5, ParameterTypeEnum.Pm10];
  }

  getBatteryPercentage(record: RecordDto): string {
    return !record || record.batteryPercentage === undefined || record.batteryPercentage < 10
      ? 'fas fa-battery-empty'
      : record.batteryPercentage < 26
        ? 'fas fa-battery-quarter'
        : record.batteryPercentage < 51
          ? 'fas fa-battery-half'
          : record.batteryPercentage < 76
            ? 'fas fa-battery-three-quarters'
            : 'fas fa-battery-full';
  }

  getWifiIcon(record: RecordDto): string {
    return !record || record.rssi === undefined || record.rssi <= -82
      ? './assets/images/wifi-signal-0.svg'
      : record.rssi > -82 && record.rssi <= -78
        ? './assets/images/wifi-signal-1.svg'
        : record.rssi > -78 && record.rssi <= -70
          ? './assets/images/wifi-signal-2.svg'
          : record.rssi > -70 && record.rssi <= -65
            ? './assets/images/wifi-signal-3.svg'
            : record.rssi > -65 && record.rssi <= -55
              ? './assets/images/wifi-signal-4.svg'
              : './assets/images/wifi-signal-4.svg';
  }

  downloadFile(blob: Blob) {
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = 'export.xlsx';
    link.click();
  }

  async createChartRange(chart: am5xy.XYChart): Promise<am5.DataItem<am5xy.IDateAxisDataItem>> {
    const {am5: am5Ns} = await this.getAmCharts5();
    const dateAxis = chart.xAxes.getIndex(0) as am5xy.DateAxis<am5xy.AxisRendererX>;

    const rangeItem = dateAxis.makeDataItem({});
    dateAxis.createAxisRange(rangeItem);
    rangeItem.get('grid')?.setAll({
      stroke: am5Ns.color(this.commonService.colors.greyLight),
      strokeOpacity: 1,
      strokeWidth: 2,
    });

    return rangeItem;
  }

  async createChartSlider(chart: am5xy.XYChart, mapComponent: IMapComponent): Promise<am5.DataItem<am5xy.IDateAxisDataItem>> {
    const {am5: am5Ns, am5xy: am5xyNs} = await this.getAmCharts5();
    const root = chart.root;
    const dateAxis = chart.xAxes.getIndex(0) as am5xy.DateAxis<am5xy.AxisRendererX>;

    const rangeItem = dateAxis.makeDataItem({});
    dateAxis.createAxisRange(rangeItem);
    rangeItem.get('grid')?.setAll({
      stroke: am5Ns.color(this.commonService.colors.greyLight),
      strokeOpacity: 1,
      strokeWidth: 2,
    });
    dateAxis.get('renderer').labels.template.set('forceHidden', true);

    const handle = am5Ns.Circle.new(root, {
      radius: 7,
      fill: am5Ns.color(this.commonService.colors.greyLight),
      cursorOverStyle: 'pointer',
      draggable: true,
    });
    rangeItem.set('bullet', am5xyNs.AxisBullet.new(root, {sprite: handle}));

    handle.events.on('dragged', (event) => {
      const point = chart.plotContainer.toLocal(event.target.toGlobal({x: event.target.x(), y: 0}));
      const value = dateAxis.positionToValue(dateAxis.toAxisPosition(dateAxis.coordinateToPosition(point.x)));
      rangeItem.set('value', value);
      mapComponent.jumpToVideoTime(value);
    });

    chart.plotContainer.set('cursorOverStyle', 'pointer');
    chart.plotContainer.events.on('click', (event) => {
      const point = chart.plotContainer.toLocal({x: event.originalEvent instanceof MouseEvent ? event.originalEvent.clientX : 0, y: 0});
      const value = dateAxis.positionToValue(dateAxis.toAxisPosition(dateAxis.coordinateToPosition(point.x)));
      if (Number.isFinite(value)) {
        rangeItem.set('value', value);
        mapComponent.jumpToVideoTime(value);
      }
    });

    return rangeItem;
  }

  createChartSliderButtonEvents(rangeItem: am5.DataItem<am5xy.IDateAxisDataItem>, timestampStart: number, chart: am5xy.XYChart, disposers: am5.IDisposer[], mapComponent: IMapComponent, setRangeValue = true): am5.IDisposer[] {
    disposers.forEach(this.disposeEvent);

    const bulletSprite = rangeItem.get('bullet')?.get('sprite');
    const dateAxis = chart.xAxes.getIndex(0) as am5xy.DateAxis<am5xy.AxisRendererX>;

    const dragstartDisposer = bulletSprite?.events.on('dragstart', () => {
      mapComponent.jumpToVideoTime(0);
    });

    const dragstopDisposer = bulletSprite?.events.on('dragstop', (event) => {
      const point = chart.plotContainer.toLocal(event.target.toGlobal({x: event.target.x(), y: 0}));
      const value = dateAxis.positionToValue(dateAxis.toAxisPosition(dateAxis.coordinateToPosition(point.x)));
      rangeItem.set('value', value);
      mapComponent.jumpToVideoTime(value);
    });

    if (setRangeValue) {
      rangeItem.set('value', timestampStart);
    }

    return [dragstartDisposer, dragstopDisposer].filter((d): d is am5.IDisposer => !!d);
  }

  async createComplianceChart(chartType: number, chartData: Record<string, unknown>[]): Promise<void> {
    const {am5: am5Ns, am5xy: am5xyNs} = await this.getAmCharts5();
    const lineColor1 = this.commonService.colors.primary;
    const lineColor2 = this.commonService.colors.red;
    const lineColor3 = this.commonService.colors.green;

    if (chartType === 1 && (chartData && chartData.length > 0)) {
      const root = this.createRoot('complianceChart');
      const chart = root.container.children.push(am5xyNs.XYChart.new(root, {}));
      const legend = chart.children.push(am5Ns.Legend.new(root, {}));

      chart.set('colors', am5Ns.ColorSet.new(root, {step: 2}));

      const dateAxis = chart.xAxes.push(am5xyNs.DateAxis.new(root, {
        renderer: am5xyNs.AxisRendererX.new(root, {minGridDistance: 50}),
        baseInterval: {timeUnit: 'minute', count: 1},
        min: new Date(2022, 5, 12, 10).getTime(),
        max: new Date(2022, 5, 12, 17).getTime(),
      }));
      dateAxis.set('dateFormats', {...dateAxis.get('dateFormats'), hour: 'h a'});

      const valueAxis = chart.yAxes.push(am5xyNs.ValueAxis.new(root, {
        renderer: am5xyNs.AxisRendererY.new(root, {minGridDistance: 50}),
        min: 0,
        max: 1,
        numberFormat: '# %',
      }));

      const thresholdItem = valueAxis.makeDataItem({value: 0.5});
      valueAxis.createAxisRange(thresholdItem);
      thresholdItem.get('grid')?.setAll({
        stroke: am5Ns.color(this.commonService.colors.red),
        strokeOpacity: 0.6,
        strokeWidth: 2,
        strokeDasharray: [10, 8],
      });

      const makeSeries = (valueField: string, name: string, color: string) => {
        const series = chart.series.push(am5xyNs.LineSeries.new(root, {
          name,
          xAxis: dateAxis,
          yAxis: valueAxis,
          valueXField: 'date',
          valueYField: valueField,
          stroke: am5Ns.color(color),
          fill: am5Ns.color(color),
          tooltip: am5Ns.Tooltip.new(root, {
            labelText: `[bold]{date.formatDate()}:[/] {${valueField}}`,
            pointerOrientation: 'vertical',
          }),
        }));
        series.strokes.template.set('strokeWidth', 2);
        series.set('minBulletDistance', 10);
        series.data.setAll(chartData);
        legend.data.push(series);
        return series;
      };

      const complianceSeries = [
        makeSeries('value1', 'Noise', lineColor1),
        makeSeries('value2', 'Gas', lineColor3),
        makeSeries('value3', 'Dust', lineColor2),
      ];

      const complianceCursor = am5xyNs.XYCursor.new(root, {xAxis: dateAxis, behavior: 'zoomX'});
      complianceCursor.set('snapToSeries', complianceSeries);
      chart.set('cursor', complianceCursor);
    }

    if (chartType === 2 && (chartData && chartData.length > 0)) {
      const root = this.createRoot('complianceChart');
      const chart = root.container.children.push(am5xyNs.XYChart.new(root, {}));

      chart.set('colors', am5Ns.ColorSet.new(root, {step: 2}));

      const legend = chart.children.push(am5Ns.Legend.new(root, {position: 'relative'}));
      legend.labels.template.set('maxWidth', 95);

      const xAxis = chart.xAxes.push(am5xyNs.CategoryAxis.new(root, {
        categoryField: 'category',
        renderer: am5xyNs.AxisRendererX.new(root, {cellStartLocation: 0.1, cellEndLocation: 0.9}),
      }));
      xAxis.data.setAll(chartData);

      const yAxis = chart.yAxes.push(am5xyNs.ValueAxis.new(root, {
        renderer: am5xyNs.AxisRendererY.new(root, {}),
        min: 0,
        max: 1,
        numberFormat: '# %',
      }));

      const makeSeries = (valueField: string, name: string, color: string) => {
        const series = chart.series.push(am5xyNs.ColumnSeries.new(root, {
          name,
          xAxis,
          yAxis,
          valueYField: valueField,
          categoryXField: 'category',
          fill: am5Ns.color(color),
          stroke: am5Ns.color(color),
          clustered: true,
        }));
        series.data.setAll(chartData);
        legend.data.push(series);
        return series;
      };

      makeSeries('noise', 'Noise', this.commonService.colors.greyLight);
      makeSeries('gas', 'Gas', this.commonService.colors.lightPurple);
      makeSeries('dust', 'Dust', this.commonService.colors.primary);
    }
  }

  async createAlarmActivityChart(chartData: Record<string, unknown>[]): Promise<void> {
    const {am5xy: am5xyNs} = await this.getAmCharts5();
    const root = this.createRoot('alarm-activity');
    const chart = root.container.children.push(am5xyNs.XYChart.new(root, {}));

    const categoryAxis = chart.xAxes.push(am5xyNs.CategoryAxis.new(root, {
      categoryField: 'category',
      renderer: am5xyNs.AxisRendererX.new(root, {minGridDistance: 1}),
    }));
    categoryAxis.get('renderer').grid.template.set('location', 0);
    categoryAxis.data.setAll(chartData);

    const valueAxis = chart.yAxes.push(am5xyNs.ValueAxis.new(root, {
      renderer: am5xyNs.AxisRendererY.new(root, {minGridDistance: 1}),
      min: 0,
      max: 10,
    }));

    const makeSeries = (open: string, close: string, name: string, fillColor: string) => {
      const series = chart.series.push(am5xyNs.ColumnSeries.new(root, {
        name,
        xAxis: categoryAxis,
        yAxis: valueAxis,
        valueYField: close,
        openValueYField: open,
        categoryXField: 'category',
        clustered: false,
        fill: this._am5!.color(fillColor),
      }));
      series.columns.template.setAll({strokeWidth: 0, width: this._am5!.percent(90)});
      series.data.setAll(chartData);
      return series;
    };

    makeSeries('open1', 'close1', 'High', this.commonService.colors.yellow);
    makeSeries('open2', 'close2', 'Medium', this.commonService.colors.red);
  }

  async setPieChart(divId: string, data: { noise: number; gas: number; dust: number }): Promise<void> {
    const {am5: am5Ns, am5percent: am5percentNs} = await this.getAmCharts5();
    const root = this.createRoot(divId);
    const chart = root.container.children.push(am5percentNs.PieChart.new(root, {
      innerRadius: am5Ns.percent(40),
    }));

    const pieSeries = chart.series.push(am5percentNs.PieSeries.new(root, {
      valueField: 'value',
      categoryField: 'category',
      fillField: 'color',
    }));
    pieSeries.labels.template.setAll({text: '{value} \n {category}', fontWeight: 'bold'});
    pieSeries.slices.template.setAll({fillOpacity: 1, templateField: 'sliceSettings'});
    pieSeries.slices.template.states.create('hover', {scale: 1, fillOpacity: 0.5});

    pieSeries.data.setAll([
      {category: 'Noise', value: data?.noise, color: am5Ns.color(this.commonService.colors.primary)},
      {category: 'Gas', value: data?.gas, color: am5Ns.color(this.commonService.colors.greyLight)},
      {category: 'Dust', value: data?.dust, color: am5Ns.color(this.commonService.colors.lightPurple)},
    ]);
  }

  async setTrackingPieChart(chart: am5percent.PieChart, data: Record<string, number>): Promise<void> {
    const {am5: am5Ns, am5percent: am5percentNs} = await this.getAmCharts5();
    const root = chart.root;
    const trackingTypeColors: Record<string, string> = {
      Dust: this.commonService.colors.dust,
      Noise: this.commonService.colors.noise,
      Gas: this.commonService.colors.gas,
      VOC: this.commonService.colors.voc,
    };

    const chartData: { category: string; value: number; color: am5.Color }[] = [];
    for (const trackingType in data) {
      if (data.hasOwnProperty(trackingType)) {
        chartData.push({
          category: trackingType,
          value: data[trackingType],
          color: am5Ns.color(trackingTypeColors[trackingType]),
        });
      }
    }

    chart.set('innerRadius', am5Ns.percent(40));

    const pieSeries = chart.series.push(am5percentNs.PieSeries.new(root, {
      valueField: 'value',
      categoryField: 'category',
      fillField: 'color',
    }));
    pieSeries.labels.template.setAll({text: '{value} \n {category}', fontWeight: 'bold'});
    pieSeries.slices.template.set('fillOpacity', 1);
    pieSeries.slices.template.states.create('hover', {scale: 1, fillOpacity: 0.5});

    pieSeries.data.setAll(chartData);
  }

  /**
   * v5 gauge best-effort equivalent of v4's am4charts.GaugeChart + chart.hands. amCharts5 has
   * no direct GaugeChart/ClockHand-on-a-value class the way v4 did — v5 gauges are hand-built
   * from a RadarChart + a circular ValueAxis + colored axis ranges + a ClockHand container
   * rotated to the current value. That manual rotation math is the one piece of v4 behavior
   * this reimplements rather than a 1:1 API swap.
   */
  async setGaugeChart(divId: string, handValue: number): Promise<void> {
    const {am5: am5Ns, am5xy: am5xyNs, am5radar: am5radarNs} = await this.getAmCharts5();
    const root = this.createRoot(divId);
    const chart = root.container.children.push(am5radarNs.RadarChart.new(root, {
      panX: false,
      panY: false,
      startAngle: 180,
      endAngle: 360,
      innerRadius: am5Ns.percent(80),
    }));

    const axisRenderer = am5radarNs.AxisRendererCircular.new(root, {
      strokeOpacity: 0,
    });
    axisRenderer.labels.template.set('forceHidden', true);
    axisRenderer.grid.template.set('forceHidden', true);

    const axis = chart.xAxes.push(am5xyNs.ValueAxis.new(root, {
      renderer: axisRenderer,
      min: 0,
      max: 100,
      strictMinMax: true,
    }));

    const addRange = (from: number, to: number, color: string, label: string) => {
      const rangeItem = axis.makeDataItem({value: from, endValue: to});
      axis.createAxisRange(rangeItem);
      rangeItem.get('axisFill')?.setAll({visible: true, fill: am5Ns.color(color), fillOpacity: 1});
      rangeItem.get('label')?.set('text', label);
    };

    addRange(0, 40, this.commonService.colors.green, 'Low');
    addRange(40, 90, this.commonService.colors.red, 'Medium');
    addRange(90, 100, this.commonService.colors.red, 'High');

    const clockHand = am5radarNs.ClockHand.new(root, {
      pinRadius: 10,
      radius: am5Ns.percent(90),
      bottomWidth: 10,
      topWidth: 1,
    });
    clockHand.hand.set('fill', am5Ns.color(this.commonService.colors.black));
    clockHand.pin.set('fill', am5Ns.color(this.commonService.colors.black));
    chart.radarContainer.children.push(clockHand);

    const positionHand = () => {
      const position = axis.valueToPosition(handValue);
      const angle = axisRenderer.positionToAngle(position);
      clockHand.set('rotation', angle);
    };
    chart.events.on('boundschanged', positionHand);
    positionHand();
  }

  async setDashboardExposureByTaskChart(chart: am5percent.PieChart, divId: string, data: readonly object[], dataType: string): Promise<am5percent.PieChart> {
    const {am5percent: am5percentNs} = await this.getAmCharts5();
    const root = this.createRoot(divId);
    chart = root.container.children.push(am5percentNs.PieChart.new(root, {
      innerRadius: this._am5!.percent(50),
    }));

    const pieSeries = chart.series.push(am5percentNs.PieSeries.new(root, {
      valueField: 'massContributionPercent',
      categoryField: dataType == 'group' ? 'taskGroupName' : 'taskName',
    }));

    pieSeries.labels.template.setAll({maxWidth: 60, oversizedBehavior: 'wrap'});
    pieSeries.slices.template.setAll({
      stroke: this._am5!.color(this.commonService.colors.white),
      strokeWidth: 2,
      strokeOpacity: 1,
    });
    pieSeries.labels.template.set('text', "{valuePercentTotal.formatNumber('#.0')}%");

    pieSeries.data.setAll(data as Record<string, unknown>[]);
    return chart;
  }

  /**
   * Bullet on each data point that reveals a per-comment badge and, on click, opens the
   * ChartAnnotationPopupComponent (single-comment mode) anchored at the click position — v5
   * replacement for v4's series.openModal-based individual-comment display/edit/delete popup.
   */
  private async addLabelBulletForIndividualComment(series: am5xy.LineSeries): Promise<void> {
    const {am5: am5Ns} = await this.getAmCharts5();
    const root = series.root;

    series.bullets.push((r, s, dataItem) => {
      const dataContext = dataItem?.dataContext as SensorRecord;
      const dataFields = {dateX: 'timestamp', valueY: series.get('valueYField') as string};
      const commentEntry = dataContext && dataFields.valueY
        ? this.comments?.[dataContext[dataFields.dateX as keyof SensorRecord] as unknown as string]?.[dataContext[dataFields.valueY as keyof SensorRecord] as unknown as string]
        : undefined;

      if (!commentEntry?.length) {
        return undefined;
      }

      const label = am5Ns.Label.new(root, {
        text: `ID : ${commentEntry[0].id ?? '1'}`,
        centerX: am5Ns.p50,
        centerY: am5Ns.p50,
        dy: 10,
        paddingTop: 4,
        paddingBottom: 4,
        paddingLeft: 8,
        paddingRight: 8,
        fill: am5Ns.color(this.commonService.colors.white),
        background: am5Ns.RoundedRectangle.new(root, {
          cornerRadiusTL: 5,
          cornerRadiusTR: 5,
          cornerRadiusBL: 5,
          cornerRadiusBR: 5,
          fill: am5Ns.color(this.commonService.colors.primary),
          stroke: am5Ns.color(this.commonService.colors.greyLight),
          strokeOpacity: 1,
        }),
        cursorOverStyle: 'pointer',
      });

      label.events.on('click', (event) => {
        const comment = commentEntry[0];
        const point = event.target.toGlobal({x: 0, y: 0});
        this.openSingleCommentPopup(comment, point.x, point.y);
      });

      return am5Ns.Bullet.new(root, {sprite: label});
    });
  }

  private openSingleCommentPopup(comment: Comment, clientX: number, clientY: number): void {
    const {overlayRef, componentRef} = this.openAnnotationOverlay(clientX, clientY, 'single', comment, []);
    componentRef.instance.saved.subscribe((updated: Comment) => {
      this.updateComment(updated, () => overlayRef.dispose());
    });
    componentRef.instance.deleted.subscribe((toDelete: Comment) => {
      this.deleteComment(toDelete, () => overlayRef.dispose());
    });
  }

  private async addGroupCommentsLabelBullet(dateAxis: am5xy.DateAxis<am5xy.AxisRendererX>, series: am5xy.LineSeries): Promise<void> {
    let keys = Object.keys(this.comments ?? {});
    keys.forEach((mainKey) => {
      const yAxisComments = this.comments[mainKey];
      const keys2 = Object.keys(yAxisComments);
      keys2.forEach((key) => {
        this.commentsCount[mainKey + '-' + key] = (this.commentsCount[mainKey + '-' + key] ?
          (this.commentsCount[mainKey + '-' + key] + yAxisComments[key].length) : yAxisComments[key].length);
      });
    });
    keys = Object.keys(this.commentsCount);
    for (let index = 0; index < keys.length; index++) {
      const commentsCountKey = keys[index];
      const startDate = Number(commentsCountKey.split('-')[0]);
      const endDate = Number(commentsCountKey.split('-')[1]);
      this.commentsRange[commentsCountKey] = await this.createRangeWithBullet(startDate, endDate,
        this.commentsCount[commentsCountKey], dateAxis, series, commentsCountKey);
    }
  }

  private async createRangeWithBullet(startDate: number, endDate: number, count: number, dateAxis: am5xy.DateAxis<am5xy.AxisRendererX>, series: am5xy.LineSeries, commentsCountKey: string): Promise<am5.DataItem<am5xy.IDateAxisDataItem>> {
    const {am5: am5Ns, am5xy: am5xyNs} = await this.getAmCharts5();
    const root = dateAxis.root;
    const commentKeyArr = commentsCountKey.split('-');
    let htmlText = `${count}`;
    let scrollToCommentByDefault = false;
    let textColor = this.commonService.colors.black;
    let commentId = '';
    if (startDate - endDate == 0) {
      startDate = startDate - 150000;
      endDate = endDate + 150000;
    }
    if (commentKeyArr && commentKeyArr.length == 2) {
      const comments = this.comments[commentKeyArr[0]][commentKeyArr[1]];
      if (comments && comments.length == 1) {
        commentId = comments[0].id;
        htmlText = '';
        scrollToCommentByDefault = true;
        textColor = this.commonService.colors.yellow;
      }
    }

    const rangeItem = dateAxis.makeDataItem({value: startDate, endValue: endDate});
    dateAxis.createAxisRange(rangeItem);
    rangeItem.get('axisFill')?.setAll({
      visible: false,
      fill: am5Ns.color(this.commonService.colors.yellow),
      fillOpacity: 0,
    });
    rangeItem.get('label')?.set('visible', false);

    const groupLabel = am5Ns.Label.new(root, {
      text: `${htmlText}`,
      fill: am5Ns.color(textColor),
      centerX: am5Ns.p50,
      centerY: am5Ns.p50,
      dy: 10,
      cursorOverStyle: 'pointer',
      visible: false,
    });
    rangeItem.set('bullet', am5xyNs.AxisBullet.new(root, {sprite: groupLabel}));

    groupLabel.events.on('click', (event) => {
      const point = event.target.toGlobal({x: 0, y: 0});
      if (scrollToCommentByDefault) {
        this.scrollToComment(commentId, commentsCountKey);
      } else {
        this.openGroupCommentsPopup(startDate, endDate, commentsCountKey, point.x, point.y);
      }
    });

    void series;
    return rangeItem;
  }

  private scrollToComment(id: string, commentsCountKey: string): void {
    this.clearNotes.next(true);
    const keys = Object.keys(this.commentsRange);
    keys.forEach((key) => {
      const rangeItem = this.commentsRange[key];
      if (!rangeItem) {
        return;
      }
      if (key == commentsCountKey) {
        rangeItem.get('axisFill')?.set('fillOpacity', 0.3);
        rangeItem.get('axisFill')?.set('visible', true);
      } else {
        rangeItem.get('axisFill')?.set('fillOpacity', 0);
        rangeItem.get('axisFill')?.set('visible', false);
      }
    });
    const comments = document.querySelectorAll('.comments-notes-div .comment-list');
    for (let i = 0; i < comments.length; i++) {
      (comments[i] as HTMLElement).style.backgroundColor = 'unset';
    }
    const commentHTML = document.getElementById(`c-${id}`);
    if (commentHTML) {
      commentHTML.style.backgroundColor = 'rgb(247,240,10,0.3)';
      commentHTML.scrollIntoView({behavior: 'smooth', block: 'end', inline: 'nearest'});
    }
  }

  private openGroupCommentsPopup(startDate: number, endDate: number, commentsCountKey: string, clientX: number, clientY: number): void {
    this.clearNotes.next(true);

    const matchingComments: Comment[] = [];
    const keys = Object.keys(this.comments ?? {});
    keys.forEach((mainKey) => {
      if (startDate == Number(mainKey)) {
        const yAxisComments = this.comments[mainKey];
        const keys2 = Object.keys(yAxisComments);
        keys2.forEach((key) => {
          if (endDate == Number(key)) {
            matchingComments.push(...yAxisComments[key]);
          }
        });
      }
    });

    const {overlayRef, componentRef} = this.openAnnotationOverlay(clientX, clientY, 'list', null, matchingComments);
    componentRef.instance.commentSelected.subscribe((comment: Comment) => {
      this.scrollToComment(comment.id, commentsCountKey);
      overlayRef.dispose();
    });

    const keys3 = Object.keys(this.commentsRange);
    keys3.forEach((key) => {
      const rangeItem = this.commentsRange[key];
      if (!rangeItem) {
        return;
      }
      if (key == commentsCountKey) {
        rangeItem.get('axisFill')?.set('fillOpacity', 0.3);
        rangeItem.get('axisFill')?.set('visible', true);
      } else {
        rangeItem.get('axisFill')?.set('fillOpacity', 0);
        rangeItem.get('axisFill')?.set('visible', false);
      }
    });
  }

  /**
   * Opens ChartAnnotationPopupComponent inside a CDK Overlay anchored at viewport (clientX,
   * clientY) coordinates — the v5 replacement for series.openModal's popup.left/popup.top
   * positioning. Uses the CDK Overlay service directly (per the migration plan) rather than
   * OverlayService, since OverlayService only supports a globally-centered position strategy
   * and template portals, not an anchored-at-a-point ComponentPortal.
   */
  private openAnnotationOverlay(clientX: number, clientY: number, mode: 'single' | 'list', comment: Comment | null, comments: Comment[]) {
    const positionStrategy = this.overlay.position()
      .global()
      .left(`${clientX}px`)
      .top(`${clientY}px`);

    const overlayRef = this.overlay.create({
      positionStrategy,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
    });

    const portal = new ComponentPortal(ChartAnnotationPopupComponent);
    const componentRef = overlayRef.attach(portal);
    componentRef.instance.mode = mode;
    componentRef.instance.comment = comment;
    componentRef.instance.comments = comments;

    const close = () => overlayRef.dispose();
    componentRef.instance.closed.subscribe(close);
    overlayRef.backdropClick().subscribe(close);

    return {overlayRef, componentRef};
  }

  private callCreateNotes(notes: Note): void {
    this.noteService.create(notes).subscribe({
      next: () => {
        if (this.refreshUI) {
          this.refreshUI.next(true);
        }
      },
      error: () => {
      }
    });
  }

  private deleteComment(comment: Comment, onDone?: () => void): void {
    this.commentService.delete(comment.id).subscribe({
      next: () => {
        onDone?.();
        if (this.refreshUI) {
          this.refreshUI.next(true);
        }
      },
      error: () => onDone?.(),
    });
  }

  getSeriesColor(index: number): am5.Color | undefined {
    if (!this._am5) {
      return undefined;
    }
    const commonColors = this.commonService.colors;
    switch (index) {
      case 0:
        return this._am5.color(commonColors.primary);
      default:
        return undefined;
    }
  }
}
